import { IInitialState } from './interfaces/state';
import {
    createSlice,
    current,
    PayloadAction,
} from '@reduxjs/toolkit';
import {
    AbstractedHardwareCategory,
    AbstractedHardwareDetailGroupResponse,
    PriceType,
} from '@vfde-sails/glados-v2';
import { AtLeast } from '@vfde-sails/types';
import { toggleArrayItem } from '@vfde-sails/utils';
import { IAccordionItemProperties } from '@vfde-brix/ws10/accordion';
import { getDeviceOverview } from './api/glados';
import {
    PageViewTrackingData,
    TrackType,
} from '@vfde-sails/tracking';

const initialState: IInitialState = {
    isFirstPage: false,
    isLastPage: false,
    selectedCategories: [],
    selectedProductId: null,
    paymentMethod: null,
};

const stoneDopSlice = createSlice({
    name: 'Stone DOP',
    initialState,
    reducers: create => ({
        setInitialState: create.preparedReducer(
            (
                categories: string[],
                paymentMethod: PriceType | null,
            ) => ({
                payload: {
                    categories,
                    paymentMethod,
                },
            }),
            (
                state,
                action: PayloadAction<{ categories: string[]; paymentMethod: PriceType | null }>,
            ) => {
                state.selectedCategories = action.payload.categories.map(category => ({
                    label: category,
                }));
                state.paymentMethod = action.payload.paymentMethod;
            },
        ),
        toggleCategory: create.reducer<AtLeast<AbstractedHardwareCategory, 'label'>>((state, action) => {
            state.selectedCategories = toggleArrayItem(
                current(state.selectedCategories),
                action.payload,
                category => category.label === action.payload.label,
            );
        }),
        // ‚TODO: Is it correct to replace legacyGroupId with defaultAtomicId?
        selectProduct: create.reducer<AbstractedHardwareDetailGroupResponse['legacyModelId']>((state, action) => {
            state.selectedProductId = action.payload;
        }),
        toggleAccordionItem: create.reducer<Pick<IAccordionItemProperties, 'optOpen' | 'stdHeadline' | 'stdId'>>(() => {}),
        trackPageView: create.preparedReducer((
            eventType: TrackType,
            eventPayload: PageViewTrackingData,
        ) => ({
            payload: {
                trackingData: {
                    eventType,
                    eventPayload,
                },
            },
        }), () => {}),
    }),
    selectors: {
        selectSelectedCategories: (state): IInitialState['selectedCategories'] => state.selectedCategories,
        selectSelectedProductId: (state): IInitialState['selectedProductId'] => state.selectedProductId,
        selectPaymentMethod: (state): IInitialState['paymentMethod'] => state.paymentMethod,
    },
    extraReducers: builder => {
        builder
            .addMatcher(
                // TODO: normally it's enough to write the matcher like this: `getDeviceOverview.matchFulfilled`
                //  but Jest spyOn can't mock the return value like this, because it's not invoked.
                //  I've found no other way to unit test a RTK Query extraReducer so far.
                action => getDeviceOverview.matchFulfilled(action),
                (state, action) => {
                    const { categories } = action.payload;

                    if (categories && categories.length > 0) {
                        state.selectedCategories = current(state.selectedCategories)
                            .map(selectedCategory =>
                                categories.find(category => category.label === selectedCategory.label),
                            )
                            .filter(Boolean) as AbstractedHardwareCategory[];
                    }
                },
            )
        ;
    },
});

export default stoneDopSlice;

export const {
    setInitialState,
    toggleCategory,
    selectProduct,
    toggleAccordionItem,
    trackPageView,
} = stoneDopSlice.actions;

export const {
    selectSelectedProductId,
    selectSelectedCategories,
    selectPaymentMethod,
} = stoneDopSlice.selectors;

/**
 * Action Dispatchers
 */
export type ActionDispatchers = Pick<
    typeof stoneDopSlice.actions,
    'setInitialState' | 'toggleAccordionItem' | 'toggleCategory' | 'selectProduct' | 'trackPageView'
>;
